<script setup lang="ts">
import { computed } from "vue";
import debounce from "lodash/debounce";
import isUndefined from "lodash/isUndefined";
import DimensionInput from "@/components/UI/DimensionInput.vue";
import TextToggle from "./TextToggle.vue";
import FontSelect from "./FontSelect.vue";
import { useStore } from "vuex";
import { addSizeToDataLayer } from "@/functions/analytics";
import ShapeInputFieldsWrapper from "@/components/UI/ShapeInputFieldsWrapper.vue";
import InputLabel from "@/components/UI/InputLabel.vue";
import TextNotice from "@/components/UI/TextNotice.vue";
import { Props } from "@/components/ShapeType.vue";
import InfoCircleDark from "@/components/icons/InfoCircleDark.vue";

const store = useStore();
const { isCutout, action, prefix, modifierAction } = defineProps<Props>();

const dimensionLabel = computed(() => store.getters.dimensionLabel);
const isDimensionValid = computed(() => store.getters.isShapeValid( isCutout ) );

const dimensions = computed(() => store.getters.getMeasurements( 'text', isCutout ) );
const modifiers = computed(() => store.getters.getModifiers( isCutout ));
const isLoading = computed(() => store.getters.fontIsLoading );

const font = computed({
	get: () => modifiers.value.font,
	set: async(value: string) => {
		store.commit("updateLoadingState", true);
		store.commit(modifierAction, { modifier: "font", value });
		store.commit("setSelectedFont", value);
		store.commit("updateLoadingState", false);
	},
});

const text = computed({
	get: () => modifiers.value.text,
	set: async(value: string) => {
		store.commit("updateLoadingState", true);
		store.commit(modifierAction, { modifier: "text", value });
		store.commit("updateLoadingState", false);
	},
});

const desiredLength = computed({
	get: () => {
		if ( isUndefined( modifiers.value.text )|| isUndefined( dimensions.value ) ) {
			return 20;
		}

		return dimensions.value.desired_length;
	},
	set: async(value: string) => {
		store.commit("updateLoadingState", true);
		await store.dispatch(action, { dimension: "desired_length", value: value });
		store.commit("updateLoadingState", false);
	},
});

const desiredDimension = computed({
	get: () => modifiers.value.desired_dimension,
	set: async(value: string) => {
		store.commit("updateLoadingState", true);
		store.commit(modifierAction, { modifier: "desired_dimension", value });
		store.commit("updateLoadingState", false);
	},
});

</script>
<template>
	<shape-input-fields-wrapper class="text-dimension-inputs">
		<div class="text-dimension-inputs__full">
			<input-label
				for="measurements-text-font"
				class="text-dimension-inputs__label"
			>{{ dimensionLabel("font", "text") }}</input-label>
			<FontSelect
				id="measurements-text-font"
				v-model="font"
				name="measurements[modifiers][font]"
				key="text-font"
			/>
		</div>

		<dimension-input
			class="text-dimension-inputs__full"
			id="measurements-text-text"
			name="measurements[modifiers][text]"
			:label="dimensionLabel('text', 'text')"
			v-model="text"
			:onBlur=" () => addSizeToDataLayer('text', +text, 'text')"
			:invalid="!isDimensionValid('text-text')"
			:isLoading="isLoading"
			:readonly="isLoading === true"
			inputmode="text"
			:delay="0"
		/>

		<dimension-input
			id="measurements-text-desired_length"
			name="measurements[dimensions][desired_length]"
			:label="dimensionLabel(desiredDimension, 'text')"
			v-model="desiredLength"
			:onBlur=" () => addSizeToDataLayer('desired_length', +desiredLength, 'text')"
			:invalid="!isDimensionValid('text-desired_length')"
			unit="cm"
		/>

		<div>
			<input-label
				for="measurements-text-desired_dimension-height"
				class="text-dimension-inputs__label"
			>{{ dimensionLabel("desired_dimension", "text") }}</input-label>
			<TextToggle
				name="measurements[modifiers][desired_dimension]"
				idPrefix="measurements-text-desired_dimension"
				valueA="height"
				valueB="width"
				v-model="desiredDimension"
				:labelA="$i18n.dimensionNames.height"
				:labelB="$i18n.dimensionNames.width"
			/>
		</div>

		<text-notice class="text-dimension-inputs__full" severity="notice">
			<template #icon>
				<info-circle-dark></info-circle-dark>
			</template>
			{{ $i18n.text.height_notice }}
		</text-notice>
	</shape-input-fields-wrapper>
</template>
<style scoped lang="scss">
.text-dimension-inputs__full {
	grid-column: span 2 / span 2;
}

.text-dimension-inputs__label {
	color: $brand-blue-dark;
	margin-bottom: 10px;
}

</style>
